import { forwardRef, type Ref } from "react";

import { cn } from "~/lib/utils";

export type DayStripParts = {
  readonly dayNumber: number;
  readonly monthShort: string;
  readonly weekdayLong: string;
  readonly isToday: boolean;
};

export function parseDayStripParts(
  dayKey: string,
  locale: string,
  todayKey: string,
): DayStripParts | null {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return {
    dayNumber: d,
    monthShort: date.toLocaleDateString(locale, { month: "short" }).replace(/\.$/, ""),
    weekdayLong: date.toLocaleDateString(locale, { weekday: "long" }).replace(/\.$/, ""),
    isToday: dayKey === todayKey,
  };
}

export const ActivitiesDayStripCell = forwardRef(function ActivitiesDayStripCell(
  {
    dayKey,
    locale,
    todayKey,
    active,
    hasEvents,
    needsAttention = false,
    onSelect,
  }: {
    readonly dayKey: string;
    readonly locale: string;
    readonly todayKey: string;
    readonly active: boolean;
    readonly hasEvents: boolean;
    /** The day has items still waiting on the user (To-do lane) — the dot goes amber. */
    readonly needsAttention?: boolean;
    readonly onSelect: () => void;
  },
  ref: Ref<HTMLButtonElement>,
) {
  const parts = parseDayStripParts(dayKey, locale, todayKey);
  if (!parts) return null;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      className={cn(
        "relative flex min-h-[4.75rem] w-[4.75rem] shrink-0 snap-center flex-col items-center justify-center gap-1 rounded-xl border px-1 py-2 text-center transition-[colors,box-shadow,transform] duration-200 sm:w-[5.25rem]",
        active
          ? "border-border bg-card text-foreground shadow-sm ring-1 ring-border/60"
          : "border-transparent text-muted-foreground hover:bg-muted/35 hover:text-muted-foreground",
      )}
    >
      {hasEvents ? (
        <span
          className={cn(
            "absolute end-2 top-2 size-1.5 rounded-full",
            // Amber = this day has something still waiting on you; neutral = just past activity.
            needsAttention
              ? "bg-amber-500"
              : active
                ? "bg-foreground/70"
                : "bg-muted-foreground/45",
          )}
          aria-hidden
        />
      ) : null}
      <div className="flex items-baseline justify-center gap-1 leading-none">
        <span
          className={cn(
            "tabular-nums tracking-tight",
            active
              ? "text-xl font-semibold text-foreground sm:text-2xl"
              : "text-lg font-medium text-muted-foreground/80 sm:text-xl",
          )}
        >
          {parts.dayNumber}
        </span>
        <span
          className={cn(
            "text-[10px] font-semibold uppercase tracking-wide sm:text-[11px]",
            active ? "text-muted-foreground" : "text-muted-foreground/65",
          )}
        >
          {parts.monthShort}
        </span>
      </div>
      <span
        className={cn(
          "max-w-full truncate px-0.5 text-[10px] font-medium capitalize sm:text-[11px]",
          active ? "text-muted-foreground" : "text-muted-foreground/60",
        )}
      >
        {parts.weekdayLong}
      </span>
    </button>
  );
});
